import { ThemeConfig } from './types';

/**
 * Dynamically loads a theme configuration by name
 * Uses dynamic import to load theme config only when needed
 * 
 * @param themeName - Name of the theme to load (must match folder name in src/themes/)
 * @returns Promise resolving to ThemeConfig or null if theme not found
 */
export async function loadThemeConfig(themeName: string): Promise<ThemeConfig | null> {
  if (!themeName) {
    return null;
  }

  try {
    // Dynamic import of theme configuration
    // Path: src/themes/[themeName]/theme.config.ts
    const themeModule = await import(`./${themeName}/theme.config`);
    
    // Theme config should be default export
    const config: ThemeConfig = themeModule.default || themeModule.themeConfig;
    
    if (!config || !config.name) {
      console.warn(`Invalid theme config for "${themeName}"`);
      return null;
    }
    
    return config;
  } catch (error) {
    console.warn(`Failed to load theme "${themeName}":`, error);
    return null;
  }
}

/**
 * Injects CSS variables into the document root
 * 
 * @param variables - Object containing CSS custom property key-value pairs
 */
export function injectCSSVariables(variables: Record<string, string>): void {
  if (typeof window === 'undefined') {
    return;
  }

  const root = document.documentElement;
  
  Object.entries(variables).forEach(([key, value]) => {
    // Ensure CSS variable names start with --
    const varName = key.startsWith('--') ? key : `--${key}`;
    root.style.setProperty(varName, value);
  });
}

/**
 * Removes previously injected CSS variables
 * 
 * @param variables - Object containing CSS custom property keys to remove
 */
export function removeCSSVariables(variables: Record<string, string>): void {
  if (typeof window === 'undefined') {
    return;
  }

  const root = document.documentElement;
  
  Object.keys(variables).forEach((key) => {
    const varName = key.startsWith('--') ? key : `--${key}`;
    root.style.removeProperty(varName);
  });
}

/** Prefix shared by every CSS custom property a theme config declares. */
const THEME_VARIABLE_PREFIX = '--theme-';

/** Prefix of the link element ids created by `getThemeStylesheetId`. */
const THEME_STYLESHEET_ID_PREFIX = 'theme-stylesheet-';

/**
 * Removes every theme variable currently set on the document root, whichever theme put it there.
 *
 * Themes do not declare identical variable sets, so removing only the keys of the config we
 * believe is active leaves the previous brand's extra variables behind and blends the two
 * themes together.
 */
export function resetThemeCSSVariables(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const themeVariables: string[] = [];

  // Collect first: removing a property while walking the live declaration shifts the indexes
  // of the remaining ones and would skip half of them.
  for (let index = 0; index < root.style.length; index++) {
    const property = root.style.item(index);
    if (property.startsWith(THEME_VARIABLE_PREFIX)) {
      themeVariables.push(property);
    }
  }

  themeVariables.forEach((property) => root.style.removeProperty(property));
}

/**
 * Removes the stylesheet of every theme, not just the one we last loaded
 */
export function removeAllThemeStylesheets(): void {
  if (typeof window === 'undefined') {
    return;
  }

  document
    .querySelectorAll(`link[id^="${THEME_STYLESHEET_ID_PREFIX}"]`)
    .forEach((link) => link.remove());
}

/**
 * Loads and injects an external stylesheet
 * 
 * @param href - URL or path to the stylesheet
 * @param id - Unique identifier for the link element
 */
export function loadStylesheet(href: string, id: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Check if stylesheet already exists
  const existing = document.getElementById(id);
  if (existing) {
    return;
  }

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Removes a previously loaded stylesheet
 * 
 * @param id - Unique identifier of the link element to remove
 */
export function removeStylesheet(id: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const link = document.getElementById(id);
  if (link) {
    link.remove();
  }
}

/**
 * Gets the stylesheet ID for a theme
 * 
 * @param themeName - Name of the theme
 * @returns Unique stylesheet ID
 */
export function getThemeStylesheetId(themeName: string): string {
  return `${THEME_STYLESHEET_ID_PREFIX}${themeName}`;
}

